// src/components/ai/AIAssessmentProvider.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api/apiClient';

// NEW IMPORTS FOR UI - Added for the dashboard component
import { useNavigate } from 'react-router-dom';
import {
    Shield, Brain, RefreshCw, Star, CheckCircle, AlertTriangle, AlertCircle,
    Lightbulb, Clock, Zap, Target, BookOpen, TrendingUp, BarChart3, Activity, Play
} from 'lucide-react'; // Assuming you use lucide-react for icons

// 1. CONTEXT CREATION & PROVIDER LOGIC (Your original code)
// ==========================================================

const AIAssessmentContext = createContext(null);

export const useAIAssessment = () => {
    const context = useContext(AIAssessmentContext);
    if (!context) {
        throw new Error('useAIAssessment must be used within AIAssessmentProvider');
    }
    return context;
};

export const AIAssessmentProvider = ({ children }) => {
    const { currentUser, isAuthenticated } = useAuth();
    const [assessmentData, setAssessmentData] = useState(null);
    const [learningJourney, setLearningJourney] = useState(null);
    const [currentDifficulty, setCurrentDifficulty] = useState(2);
    const [responseBuffer, setResponseBuffer] = useState([]);
    const [isLoading, setIsLoading] = useState(true); // Start true for initial load

    useEffect(() => {
        if (isAuthenticated && currentUser?.id) {
            loadAssessmentData();
        } else if (!isAuthenticated) {
            setIsLoading(false); // Stop loading if not authenticated
        }
    }, [isAuthenticated, currentUser]);

    const loadAssessmentData = async () => {
        try {
            setIsLoading(true);
            const response = await apiClient.get(`/ai/journey/${currentUser.id}`);
            
            if (response.data.success) {
                const data = response.data.data || response.data;
                
                setLearningJourney(data.learning_journey || []);
                setAssessmentData({
                    competency_level: data.competency_level || 'beginner',
                    focus_areas: data.focus_areas || [],
                    assessment_date: data.assessment_date,
                    overall_score: data.overall_score,
                    strengths: data.strengths || [],
                    weaknesses: data.weaknesses || [],
                    has_assessment: data.has_assessment !== false
                });
            } else {
                setAssessmentData({
                    competency_level: 'beginner',
                    focus_areas: [],
                    has_assessment: false
                });
                setLearningJourney([]);
            }
        } catch (error) {
            console.error('Failed to load assessment data:', error);
            setAssessmentData({
                competency_level: 'beginner',
                focus_areas: [],
                has_assessment: false,
                error: error.message
            });
            setLearningJourney([]);
        } finally {
            setIsLoading(false);
        }
    };

    const recordResponse = async (responseData) => {
        // ... (rest of your original recordResponse function is unchanged)
    };

    const getNextScenario = async (topic, preferredDifficulty = null) => {
        // ... (rest of your original getNextScenario function is unchanged)
    };

    const triggerManualAssessment = async () => {
        try {
            setIsLoading(true);
            
            if (!currentUser || !currentUser.role) {
                throw new Error('User data not available for assessment');
            }

            const response = await apiClient.post('/ai/assess/initial', {
                user_data: {
                    role: currentUser.role,
                    department: currentUser.department || 'General',
                    experience_level: currentUser.experience_level || 'intermediate',
                    user_id: currentUser.id
                }
            });
            
            if (response.data.success) {
                const assessment = response.data.assessment;
                setAssessmentData({ ...assessment, has_assessment: true });
                setLearningJourney(assessment.learning_journey || []);
                return { success: true, assessment };
            } else {
                throw new Error(response.data.message || 'Assessment failed');
            }
        } catch (error) {
            console.error('Manual assessment failed:', error);
            const fallbackAssessment = createFallbackAssessment();
            setAssessmentData(fallbackAssessment);
            setLearningJourney(fallbackAssessment.learning_journey);
            return { success: true, assessment: fallbackAssessment, fallback: true };
        } finally {
            setIsLoading(false);
        }
    };
    
    const createFallbackAssessment = () => {
      // ... (rest of your original createFallbackAssessment function is unchanged)
      return { /* ... fallback data */ };
    };

    const value = {
        assessmentData,
        learningJourney,
        currentDifficulty,
        responseBuffer: responseBuffer.length,
        isLoading,
        recordResponse,
        getNextScenario,
        triggerManualAssessment,
        loadAssessmentData
    };

    return (
        <AIAssessmentContext.Provider value={value}>
            {children}
        </AIAssessmentContext.Provider>
    );
};


// 2. NEWLY INTEGRATED UI COMPONENT
// =================================================

// Placeholder for the RadarChart component
const RadarChart = ({ size = 400 }) => (
    <div style={{ height: `${size}px`, width: '100%' }} className="flex items-center justify-center bg-gray-700/50 rounded-full text-gray-400">
        <p>Radar Chart Placeholder</p>
    </div>
);

// This is a mock for dashboard-specific stats not present in the AIAssessmentContext.
const mockDashboardData = {
    stats: { challengesCompleted: 18, currentStreak: 4, longestStreak: 12 }
};

/**
 * A comprehensive dashboard component that consumes the AIAssessmentContext
 * and displays the user's cybersecurity assessment results and learning path.
 */
export const AIAssessmentDashboard = () => {
    const navigate = useNavigate();
    const {
        assessmentData,
        learningJourney,
        triggerManualAssessment,
        isLoading: assessmentLoading
    } = useAIAssessment();
    
    const dashboardData = mockDashboardData; // Using mock data for stats

    // Display a loading state while the initial data is being fetched.
    if (assessmentLoading && !assessmentData) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#102c4a] to-[#164f3f] text-white flex justify-center items-center">
                <div className="flex flex-col items-center">
                    <RefreshCw className="w-8 h-8 animate-spin text-blue-400 mb-4" />
                    <p className="text-lg">Loading Assessment Data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#102c4a] to-[#164f3f] text-white p-6">
            <div className="max-w-6xl mx-auto space-y-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-bold flex items-center">
                        <Shield className="mr-3 text-blue-500" size={28} />
                        Cybersecurity Assessment
                    </h2>
                    <p className="text-gray-400 mt-1">Your personalized security competency analysis</p>
                </div>
                <div className="flex space-x-3">
                    <button
                        onClick={triggerManualAssessment}
                        disabled={assessmentLoading}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800/50 px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
                    >
                        {assessmentLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
                        {assessmentData?.has_assessment ? 'Retake Assessment' : 'Take Assessment'}
                    </button>
                </div>
            </div>

            {/* --- Start of the Integrated UI Code --- */}

            {/* Executive Summary Card */}
            <div className="bg-gradient-to-r from-blue-900 to-purple-900 rounded-lg p-6">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-center">
                    <div className="lg:col-span-2">
                        <h3 className="text-xl font-semibold text-white mb-2">Security Risk Assessment</h3>
                        <div className="flex items-center space-x-4">
                            <div className={`px-4 py-2 rounded-full text-sm font-bold ${
                                (assessmentData?.overall_score || 0) >= 80 ? 'bg-green-500 text-white' :
                                (assessmentData?.overall_score || 0) >= 60 ? 'bg-yellow-500 text-black' :
                                'bg-red-500 text-white'
                            }`}>
                                {assessmentData?.has_assessment && assessmentData?.overall_score ? `${Math.round(assessmentData.overall_score)}% SECURE` : 'NOT ASSESSED'}
                            </div>
                            <div className="text-white/80">
                                Competency: <span className="text-white font-semibold capitalize">
                                    {assessmentData?.competency_level || 'Unknown'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 lg:col-span-2">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-white">{dashboardData?.stats?.challengesCompleted || 0}</div>
                            <div className="text-white/60 text-sm">Completed</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-white">{dashboardData?.stats?.currentStreak || 0}</div>
                            <div className="text-white/60 text-sm">Day Streak</div>
                        </div>
                    </div>
                </div>
                {assessmentData?.assessment_date && (
                    <div className="mt-4 text-white/60 text-sm">
                        Last assessed: {new Date(assessmentData.assessment_date).toLocaleDateString()}
                    </div>
                )}
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    {/* Strengths Section */}
                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center text-green-400">
                            <Star className="w-5 h-5 mr-2" /> Your Strengths
                        </h3>
                        <div className="space-y-3">
                            {assessmentData?.strengths?.length > 0 ? (
                                assessmentData.strengths.map((strength, index) => (
                                    <div key={index} className="flex items-center justify-between bg-green-900/20 p-3 rounded-lg border border-green-700/50">
                                        <div className="flex items-center">
                                            <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                                            <span className="capitalize font-medium">{strength.replace(/_/g, ' ')}</span>
                                        </div>
                                        <div className="text-green-400 font-semibold">Strong</div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-gray-400 text-center py-4">Take an assessment to discover your strengths</div>
                            )}
                        </div>
                    </div>

                    {/* Areas for Improvement Section */}
                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center text-yellow-400">
                            <AlertTriangle className="w-5 h-5 mr-2" /> Areas for Improvement
                        </h3>
                        <div className="space-y-3">
                            {assessmentData?.weaknesses?.length > 0 ? (
                                assessmentData.weaknesses.map((weakness, index) => (
                                    <div key={index} className="flex items-center justify-between bg-red-900/20 p-3 rounded-lg border border-red-700/50">
                                        <div className="flex items-center">
                                            <AlertCircle className="w-5 h-5 text-red-400 mr-3" />
                                            <span className="capitalize font-medium">{weakness.replace(/_/g, ' ')}</span>
                                        </div>
                                        <div className="text-red-400 font-semibold">Priority</div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-gray-400 text-center py-4">Take an assessment to identify improvement areas</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* AI Recommendations */}
                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center text-purple-400">
                            <Lightbulb className="w-5 h-5 mr-2" /> AI Recommendations
                        </h3>
                        <div className="space-y-4">
                            {learningJourney?.length > 0 ? (
                                learningJourney.slice(0, 3).map((rec, index) => (
                                    <div key={index} className="bg-purple-900/20 p-4 rounded-lg border border-purple-700/50">
                                        {/* ... (recommendation item UI) */}
                                        <h4 className="font-medium text-white mb-1 capitalize">{(rec.topic_name || rec.topic)?.replace(/_/g, ' ')}</h4>
                                        <div className="flex items-center text-sm text-gray-400"><Clock className="w-4 h-4 mr-1" /> {rec.estimated_scenarios || 5} scenarios</div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-gray-400 text-center py-4">Complete assessment for personalized recommendations</div>
                            )}
                        </div>
                        {learningJourney?.length > 3 && <button className="w-full mt-4 text-purple-400 hover:text-purple-300 text-sm font-medium">View All Recommendations →</button>}
                    </div>

                    {/* Quick Actions */}
                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center text-blue-400">
                            <Zap className="w-5 h-5 mr-2" /> Quick Actions
                        </h3>
                        <div className="space-y-3">
                            <button onClick={() => navigate('/challenges')} className="w-full bg-blue-600 hover:bg-blue-700 p-3 rounded-lg transition-colors flex items-center justify-center"><Target className="w-4 h-4 mr-2" /> Practice Challenges</button>
                            <button onClick={() => navigate('/learning')} className="w-full bg-green-600 hover:bg-green-700 p-3 rounded-lg transition-colors flex items-center justify-center"><BookOpen className="w-4 h-4 mr-2" /> Learning Modules</button>
                            <button onClick={() => navigate('/skill-tree')} className="w-full bg-purple-600 hover:bg-purple-700 p-3 rounded-lg transition-colors flex items-center justify-center"><TrendingUp className="w-4 h-4 mr-2" /> Skill Progress</button>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* The rest of the UI sections (Competency Analysis, Risk Assessment, etc.) */}
            {/* ... (All other JSX from your provided code would go here) ... */}

            {/* Assessment Prompt for First-Time Users */}
            {!assessmentData?.has_assessment && (
                <div className="bg-gradient-to-r from-yellow-900/50 to-orange-900/50 border border-yellow-700/50 rounded-lg p-6">
                     <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center">
                            <Brain className="w-8 h-8 text-yellow-400 mr-4" />
                            <div>
                                <h3 className="text-xl font-semibold text-yellow-200">Complete Your Assessment</h3>
                                <p className="text-yellow-200/80 mt-1">Take our AI-powered assessment to get personalized learning recommendations.</p>
                            </div>
                        </div>
                        <button
                            onClick={triggerManualAssessment}
                            disabled={assessmentLoading}
                            className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-800 px-6 py-3 rounded-lg font-medium transition-colors flex items-center"
                        >
                            {assessmentLoading ? <RefreshCw className="w-5 h-5 mr-2 animate-spin" /> : <Play className="w-5 h-5 mr-2" />}
                            Start Assessment
                        </button>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
};